import React, { useEffect, useMemo, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import { GLTFExporter, USDZExporter } from 'three-stdlib'

export default function ExportBridge({ onARExport, ready, getExcludeList }) {
  const { scene } = useThree()

  const getExcludeListRef = useRef(getExcludeList)
  useEffect(() => {
    getExcludeListRef.current = getExcludeList
  }, [getExcludeList])

  const withExcludedHidden = async (fn) => {
    const toHide = (getExcludeListRef.current?.() || []).filter(Boolean)
    const prev = new Map()
    toHide.forEach((obj) => {
      prev.set(obj, obj.visible)
      obj.visible = false
    })
    try {
      return await fn()
    } finally {
      prev.forEach((v, obj) => (obj.visible = v))
    }
  }

  const download = (filename, blob) => {
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = filename
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const MIME_GLTF_BINARY = 'model/gltf-binary'
  const MIME_USDZ = 'model/vnd.usdz+zip'

  const apiRef = useRef(null)
  if (!apiRef.current) {
    const exportGLBBlob = async () => {
      scene.updateMatrixWorld(true)
      const exporter = new GLTFExporter()
      const arrayBuffer = await withExcludedHidden(
        () =>
          new Promise((resolve, reject) => {
            exporter.parse(scene, resolve, reject, {
              binary: true,
              onlyVisible: true,
            })
          }),
      )
      return new Blob([arrayBuffer], { type: MIME_GLTF_BINARY })
    }

    const exportUSDZBlob = async () => {
      scene.updateMatrixWorld(true)
      const exporter = new USDZExporter()
      const arrayBuffer = await withExcludedHidden(() => exporter.parse(scene))
      return new Blob([arrayBuffer], { type: MIME_USDZ })
    }

    const exportGLB = async (filename = 'scene.glb') =>
      download(filename, await exportGLBBlob())
    const exportUSDZ = async (filename = 'scene.usdz') =>
      download(filename, await exportUSDZBlob())

    const exportAR = async ({
      prefer = 'usdz',
      type = 'download',
      filename,
    } = {}) => {
      const wantUSDZ = prefer === 'usdz'
      if (type === 'blob') return wantUSDZ ? exportUSDZBlob() : exportGLBBlob()
      return wantUSDZ
        ? exportUSDZ(filename || 'scene.usdz')
        : exportGLB(filename || 'scene.glb')
    }

    apiRef.current = {
      exportGLB,
      exportUSDZ,
      exportGLBBlob,
      exportUSDZBlob,
      exportAR,
    }
  }

  const sentRef = useRef(false)
  useEffect(() => {
    if (ready && !sentRef.current && typeof onARExport === 'function') {
      sentRef.current = true
      onARExport(apiRef.current)
    }
  }, [ready, onARExport])

  return null
}
